import { useCallback, useMemo } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useAuth0V2 } from "@stadium/services/auth0";
import { useMutation } from "@stadium/services/apollo-client";
import { logoutRef } from "@stadium/services/auth/client";
import { logoutCommonParamsWithGenericCallback } from "@services/auth";

import { destroySession } from "graphql/mutations/session/destroy";

import { useSloContext } from "@stadium/hooks/lib/use-slo";

import { endImpersonationSession } from "@stadium/helpers/impersonation";
import { selectedWorkspaceSlugCookie } from "@stadium/helpers/cookie";

import { COLLAPSIBLE_BANNER_PREFIX } from "@constants/banner";

const useLogout = (logoutUrl = "") => {
  const { logout: auth0Logout } = useAuth0V2();
  const { uid } = useSloContext();
  const { asPath } = useRouter();

  const [deleteSessionRequest] = useMutation(destroySession);

  const destroySessionHandler = useCallback(
    (uid) => deleteSessionRequest({ variables: { input: { uid } } }),
    [deleteSessionRequest],
  );

  const logoutUser = useCallback(
    () => auth0Logout(logoutCommonParamsWithGenericCallback(logoutUrl || asPath)),
    [auth0Logout, logoutUrl, asPath],
  );

  const removeCollapsibleBannerKeys = () => {
    Object.keys(window.sessionStorage).forEach((key) => {
      if (key.includes(COLLAPSIBLE_BANNER_PREFIX)) {
        window.sessionStorage.removeItem(key);
      }
    });
  };

  const postLogout = useCallback(() => {
    selectedWorkspaceSlugCookie.reset();
    endImpersonationSession();
    removeCollapsibleBannerKeys();
    return logoutUser();
  }, [logoutUser]);

  const logoutAction = useCallback(() => {
    if (uid) {
      logoutRef.current = true;
      destroySessionHandler(uid)
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          postLogout();
        });
    } else {
      postLogout();
    }
  }, [destroySessionHandler, uid, postLogout]);

  return useMemo(
    () => ({
      logoutAction,
    }),
    [logoutAction],
  );
};

export default useLogout;
